import React, { useState, useEffect } from "react";
import { postData, ImageFileUpload } from "../../../../utility/Utility";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Swal from "sweetalert2";
import CustomLoader from "../../../../Components/CustomLoader/CustomLoader";

function HeaderList() {
  const [headers, setHeaders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const rowsPerPage = 10;

  const fetchHeaders = async (page = 1) => {
    setLoading(true);
    try {
      const response = await postData("api/admin/header-settings/getAll", {
        page: page,
        limit: rowsPerPage
      });

      if (response?.success) {
        // Add isEditing flag to each header
        setHeaders((response.settings || []).map(header => ({
          ...header,
          isEditing: false
        })));
        setCurrentPage(response.currentPage ? response.currentPage - 1 : 0);
        setTotalPages(response.totalPages || 1);
      } else {
        toast.error(response?.message || "Failed to fetch headers");
      }
    } catch (error) {
      toast.error("Error fetching headers");
      console.error("Fetch headers error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHeaders(currentPage + 1);
  }, [currentPage]);

  const handlePageChange = ({ selected }) => {
    setCurrentPage(selected);
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        const response = await postData(`api/admin/header-settings/delete/${id}`, {});

        if (response.success) {
          toast.success("Header deleted successfully");
          fetchHeaders(currentPage + 1);
        } else {
          toast.error(response.message || "Failed to delete header");
        }
      } catch (error) {
        toast.error("Error deleting header");
        console.error("Delete header error:", error);
      }
    }
  };

  const handleStatusChange = async (header) => {
    const newStatus = header.status === "active" ? "inactive" : "active";

    const result = await Swal.fire({
      title: `Are you sure you want to ${newStatus === "active" ? "activate" : "deactivate"} this header?`,
      text: `The header will be ${newStatus === "active" ? "activated" : "deactivated"}.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: `Yes, ${newStatus === "active" ? "activate" : "deactivate"} it!`,
    });

    if (result.isConfirmed) {
      try {


        // Create FormData for the update with correct field names
        const formData = new FormData();
        formData.append('id', header._id);
        formData.append('email', header.email);
        formData.append('phone', header.phone);
        formData.append('status', newStatus);

        // If there's a logo, append it
        if (header.logo) {
          formData.append('logo', header.logo);
        }

        const response = await postData(`api/admin/header-settings/update`, formData);


        if (response.success) {
          toast.success(`Header ${newStatus === "active" ? "activated" : "deactivated"} successfully`);
          fetchHeaders(currentPage + 1);
        } else {
          toast.error(response.message || "Failed to update header status");
        }
      } catch (error) {
        toast.error(`Error ${newStatus === "active" ? "activating" : "deactivating"} header`);
        console.error("Status update error:", error);
      }
    }
  };

  const handleEdit = (id) => {
    setHeaders(prevHeaders =>
      prevHeaders.map(header =>
        header._id === id
          ? { ...header, isEditing: true }
          : header
      )
    );
  };

  const handleCancelEdit = (id) => {
    setHeaders(prevHeaders =>
      prevHeaders.map(header =>
        header._id === id
          ? { ...header, isEditing: false }
          : header
      )
    );
  };

  const handleSave = async (header) => {
    try {
      setLoading(true);

      const formData = new FormData();
      formData.append('id', header._id);
      formData.append('email', header.email);
      formData.append('phone', header.phone);
      formData.append('status', header.status);

      if (header.logo) {
        formData.append('logo', header.logo);
      }

      const response = await postData(`api/admin/header-settings/update`, formData);

      if (response.success) {
        toast.success("Header updated successfully");
        setHeaders(prevHeaders =>
          prevHeaders.map(h =>
            h._id === header._id
              ? {
                ...response.data,
                isEditing: false,
                logoPreview: undefined
              }
              : h
          )
        );
        fetchHeaders(currentPage + 1);
      } else {
        toast.error(response.message || "Failed to update header");
      }
    } catch (error) {
      toast.error("Error updating header");
      console.error("Update header error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogoChange = async (e, headerId) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const imageFormData = new FormData();
      imageFormData.append("file", file);

      const uploadResponse = await ImageFileUpload("api/admin/upload?is_upload=s3", imageFormData);

      if (uploadResponse?.success) {
        toast.success(uploadResponse.message || "Image uploaded successfully");
        setHeaders(prevHeaders =>
          prevHeaders.map(h =>
            h._id === headerId
              ? {
                ...h,
                logo: uploadResponse.url,
                logoPreview: URL.createObjectURL(file)
              }
              : h
          )
        );
      } else {
        toast.error(uploadResponse?.message || "Image upload failed");
      }
    } catch (error) {
      console.error("Image upload error:", error);
      toast.error(error.response?.data?.message || "Error uploading image");
    }
  };

  return (
    <div className="content-page">
      <ToastContainer position="top-right" autoClose={3000} />
      <CustomLoader loading={loading} />
      <div className="content">
        <div className="container-fluid">
          <div className="py-3 d-flex align-items-sm-center flex-sm-row flex-column justify-content-between">
            <div className="flex-grow-1">
              <h4 className="fs-18 fw-semibold m-0">Header Management</h4>
            </div>
            <div>
            </div>
          </div>

          <div className="row mt-2">
            <div className="col-12">
              <div className="card">
                <div className="card-body">
                  {loading ? (
                    <div className="text-center py-5">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-hover">
                        <thead>
                          <tr>
                            <th>Logo</th>
                            <th>Email</th>
                            <th>Phone</th>

                            <th width="150px">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {headers.length > 0 ? (
                            headers.map((header) => (
                              <tr key={header._id}>
                                <td>
                                  {header.isEditing ? (
                                    <div style={{ position: 'relative' }}>
                                      <img
                                        src={header.logoPreview || header.logo}
                                        style={{ width: 150, height: 100 }}
                                        onError={(e) => { e.target.onerror = null; }}
                                      />
                                      <label className="btn btn-sm btn-primary" style={{ position: 'absolute', bottom: 0, left: 0, padding: '0.15rem 0.3rem' }}>
                                        <i className="mdi mdi-pencil"></i>
                                        <input
                                          type="file"
                                          accept="image/*"
                                          onChange={(e) => handleLogoChange(e, header._id)}
                                          style={{ display: 'none' }}
                                        />
                                      </label>
                                    </div>
                                  ) : (
                                    <img
                                      src={header.logo}
                                      style={{ width: 150, height: 100 }}
                                      onError={(e) => { e.target.onerror = null; }}
                                    />
                                  )}
                                </td>
                                <td>
                                  {header.isEditing ? (
                                    <input
                                      type="text"
                                      value={header.email}
                                      onChange={(e) => {
                                        setHeaders(prevHeaders =>
                                          prevHeaders.map(h =>
                                            h._id === header._id
                                              ? { ...h, email: e.target.value }
                                              : h
                                          )
                                        );
                                      }}
                                      className="form-control form-control-sm"
                                    />
                                  ) : (
                                    header.email
                                  )}
                                </td>
                                <td>
                                  {header.isEditing ? (
                                    <input
                                      type="text"
                                      value={header.phone}
                                      onChange={(e) => {
                                        setHeaders(prevHeaders =>
                                          prevHeaders.map(h =>
                                            h._id === header._id
                                              ? { ...h, phone: e.target.value }
                                              : h
                                          )
                                        );
                                      }}
                                      className="form-control form-control-sm"
                                    />
                                  ) : (
                                    header.phone
                                  )}
                                </td>
                                <td>
                                  <div className="d-flex gap-2">
                                    {header.isEditing ? (
                                      <>
                                        <button
                                          className="btn btn-sm btn-outline-primary"
                                          onClick={() => handleSave(header)}
                                          disabled={loading}
                                        >
                                          {loading ? (
                                            <span className="spinner-border spinner-border-sm"></span>
                                          ) : (
                                            <i className="mdi mdi-content-save"></i>
                                          )}
                                        </button>
                                        <button
                                          className="btn btn-sm btn-outline-secondary"
                                          onClick={() => handleCancelEdit(header._id)}
                                          disabled={loading}
                                        >
                                          <i className="mdi mdi-close"></i>
                                        </button>
                                      </>
                                    ) : (
                                      <>
                                        <button
                                          className="btn btn-sm btn-outline-primary"
                                          onClick={() => handleEdit(header._id)}
                                        >
                                          <i className="mdi mdi-pencil"></i>
                                        </button>
                                      </>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="7" className="text-center py-4">
                                No headers found
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>

                      {totalPages > 1 && (
                        <ReactPaginate
                          previousLabel={<i className="mdi mdi-chevron-left"></i>}
                          nextLabel={<i className="mdi mdi-chevron-right"></i>}
                          breakLabel="..."
                          breakClassName="page-item"
                          breakLinkClassName="page-link"
                          pageCount={totalPages}
                          marginPagesDisplayed={2}
                          pageRangeDisplayed={5}
                          onPageChange={handlePageChange}
                          containerClassName="pagination pagination-rounded justify-content-end mt-3"
                          pageClassName="page-item"
                          pageLinkClassName="page-link"
                          previousClassName="page-item"
                          previousLinkClassName="page-link"
                          nextClassName="page-item"
                          nextLinkClassName="page-link"
                          activeClassName="active"
                          forcePage={currentPage}
                        />
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default HeaderList;